import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { getCachedOrFetch } from '../../utils/api/cache';
import { makeAuthenticatedFetch } from '../../utils/api/auth';
import { getErrorMessage } from '../../utils/errors';

// Settings type definitions and utilities
export interface Settings {
  stravaIntegrationEnabled: boolean;
  stravaAutoUploadEnabled: boolean;
  stravaLinked: boolean;
  stravaAthleteId?: number | null;
  stravaAthleteName?: string | null;
  manualMovementEnabled: boolean;
  showUserLocationOnMaps: boolean;
  notificationsEnabled: boolean;
}

const DEFAULT_SETTINGS: Settings = {
  stravaIntegrationEnabled: true,
  stravaAutoUploadEnabled: true,
  stravaLinked: false,
  stravaAthleteId: null,
  stravaAthleteName: null,
  manualMovementEnabled: false,
  showUserLocationOnMaps: false,
  notificationsEnabled: true,
};

// In-memory cache for current user's settings
let settingsCache: Settings | null = null;
let currentUserId: string | null = null;

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-7f15104b`;
const PENDING_SETTINGS_KEY = 'loop_pending_settings';

function getPendingSettingsKey(userId?: string | null): string | null {
  if (!userId) return null;
  return `${PENDING_SETTINGS_KEY}:${userId}`;
}

function readPendingSettings(userId?: string | null): Settings | null {
  try {
    const storageKey = getPendingSettingsKey(userId);
    if (!storageKey) return null;
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as Settings;
  } catch (error) {
    console.warn('⚠️ Failed to read pending settings:', error);
    return null;
  }
}

function writePendingSettings(userId: string | null | undefined, settings: Settings | null): void {
  try {
    const storageKey = getPendingSettingsKey(userId);
    if (!storageKey) return;
    if (settings) {
      localStorage.setItem(storageKey, JSON.stringify(settings));
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch (error) {
    console.warn('⚠️ Failed to write pending settings:', error);
  }
}

/**
 * Load settings from backend for a specific user
 */
export async function loadUserSettings(userId: string, accessToken: string): Promise<Settings> {
  try {
    console.log('📥 Loading settings for user:', userId);

    const settings = await getCachedOrFetch(`settings:${userId}`, async () => {
      const response = await makeAuthenticatedFetch(`${API_BASE}/settings`);

      if (!response.ok) {
        throw new Error('Failed to load settings');
      }

      const data = await response.json();
      return data.settings || DEFAULT_SETTINGS;
    });

    const pending = readPendingSettings(userId);
    settingsCache = {
      ...DEFAULT_SETTINGS,
      ...settings,
      ...(pending || {}),
    };
    currentUserId = userId;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('settings:updated', { detail: settingsCache }));
    }
    
    console.log('✅ Settings loaded for user:', userId, settingsCache);
    return settingsCache;
  } catch (error: unknown) {
    const errorMessage = getErrorMessage(error);
    if (errorMessage?.includes('Session expired')) {
      console.warn('⚠️ Session expired while loading settings - using defaults');
    } else {
      console.error('❌ Failed to load settings:', error);
    }
    const pending = readPendingSettings(userId);
    settingsCache = { ...DEFAULT_SETTINGS, ...(pending || {}) };
    currentUserId = userId;
    return settingsCache;
  }
}

/**
 * Save settings to backend for current user
 */
async function saveUserSettings(accessToken: string, settings: Settings): Promise<boolean> {
  try {
    console.log('💾 Saving settings to backend:', settings);
    
    const response = await makeAuthenticatedFetch(`${API_BASE}/settings`, {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    });

    if (!response.ok) {
      console.error('❌ Failed to save settings to backend');
      return false;
    }
    console.log('✅ Settings saved to backend');
    return true;
  } catch (error: unknown) {
    const errorMessage = getErrorMessage(error);
    if (errorMessage?.includes('Session expired')) {
      console.warn('⚠️ Session expired while saving settings - will retry later');
      return false;
    }
    console.error('❌ Failed to save settings:', error);
    return false;
  }
}

/**
 * Clear settings cache (call on logout)
 */
export function clearSettingsCache(): void {
  console.log('🧹 Clearing settings cache');
  settingsCache = null;
  currentUserId = null;
}

/**
 * Get current settings (from cache)
 * Note: You must call loadUserSettings first after login
 */
export function getSettings(): Settings {
  if (!settingsCache) {
    // Only warn if we have a current user but no cached settings
    if (currentUserId) {
      console.warn('⚠️ Settings not loaded, using defaults. Call loadUserSettings after login.');
    }
    return { ...DEFAULT_SETTINGS };
  }
  return settingsCache;
}

/**
 * Update settings (requires access token to save to backend)
 */
const updateSettings = async (accessToken: string | null, updates: Partial<Settings>): Promise<Settings> => {
  const current = getSettings();
  const updated = { ...current, ...updates };

  settingsCache = updated;

  // Dispatch event for UI updates
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('settings:updated', { detail: updated }));
  }

  // Queue for backend sync.
  // This can be called before `loadUserSettings()` finishes (so `currentUserId` isn't set yet),
  // but we still want the setting to persist across reloads/resume.
  let resolvedUserId = currentUserId;
  if (!resolvedUserId && typeof window !== 'undefined') {
    try {
      const raw = localStorage.getItem('loop_supabase_session');
      if (raw) {
        const session = JSON.parse(raw);
        resolvedUserId = session?.user?.id ?? null;
      }
    } catch {
      // ignore
    }
  }

  if (resolvedUserId) {
    currentUserId = resolvedUserId;
    writePendingSettings(resolvedUserId, updated);
    console.log('🕒 Settings queued for sync');
  } else {
    console.warn('⚠️ No user id available, settings not queued');
  }

  if (!accessToken) {
    console.warn('⚠️ No access token provided, settings will sync when available');
  }

  return updated;
};

export async function flushPendingSettings(accessToken: string | null, userId?: string | null): Promise<void> {
  if (!userId) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    console.log('📡 Offline, skipping settings flush');
    return;
  }

  const pending = readPendingSettings(userId);
  if (!pending) return;

  const saved = await saveUserSettings(accessToken, pending);
  if (saved) {
    writePendingSettings(userId, null);
  } else {
    console.log('🕒 Settings will retry on next flush');
  }
}

// Exported setter functions - these now require access token
export async function setStravaEnabled(accessToken: string | null, enabled: boolean): Promise<void> {
  await updateSettings(accessToken, { stravaIntegrationEnabled: enabled, stravaAutoUploadEnabled: enabled });
}

export async function setStravaAutoUploadEnabled(accessToken: string | null, enabled: boolean): Promise<void> {
  await updateSettings(accessToken, { stravaAutoUploadEnabled: enabled, stravaIntegrationEnabled: enabled });
}

export async function setStravaLinked(accessToken: string | null, enabled: boolean): Promise<void> {
  await updateSettings(accessToken, { stravaLinked: enabled });
}

export async function setStravaProfile(accessToken: string | null, profile: { id: number; name: string } | null): Promise<void> {
  if (!profile) {
    await updateSettings(accessToken, { stravaAthleteId: null, stravaAthleteName: null });
    return;
  }
  await updateSettings(accessToken, { stravaAthleteId: profile.id, stravaAthleteName: profile.name });
}

export async function setManualMovementEnabled(accessToken: string | null, enabled: boolean): Promise<void> {
  await updateSettings(accessToken, { manualMovementEnabled: enabled });
}

export async function setShowUserLocationOnMaps(accessToken: string | null, enabled: boolean): Promise<void> {
  await updateSettings(accessToken, { showUserLocationOnMaps: enabled });
}

// Legacy synchronous versions for backward compatibility (will use cache only)
export function setStravaLinkedSync(enabled: boolean): void {
  if (settingsCache) {
    settingsCache = { ...settingsCache, stravaLinked: enabled };
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('settings:updated', { detail: settingsCache }));
    }
  }
}

export function setStravaProfileSync(profile: { id: number; name: string } | null): void {
  if (settingsCache) {
    if (!profile) {
      settingsCache = { ...settingsCache, stravaAthleteId: null, stravaAthleteName: null };
    } else {
      settingsCache = { ...settingsCache, stravaAthleteId: profile.id, stravaAthleteName: profile.name };
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('settings:updated', { detail: settingsCache }));
    }
  }
}
